import React, { useState, useEffect } from "react";
import "./calendar.css";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { FaPlus, FaLock, FaUnlock } from "react-icons/fa";
import api from '../../service/caller';
import { useParams } from "react-router-dom";

const CalendarView = () => {
  const { doctorId } = useParams();
  const [appointments, setAppointments] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [timeRange, setTimeRange] = useState({
    startTime: "08:00",
    endTime: "18:00"
  });
  const [recurrence, setRecurrence] = useState("none");
  const [recurrenceEndDate, setRecurrenceEndDate] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().split('T')[0]);

  // Récupérer les rendez-vous, disponibilités et créneaux bloqués du médecin
  const fetchDoctorSchedule = async (date = currentDate) => {
    try {
      // 1. Récupérer les rendez-vous (ne peuvent pas être modifiés)
      const appointmentsResponse = await api.get(`/clinic/reservedSlots?doctorId=${doctorId}`);
      if (Array.isArray(appointmentsResponse.data)) {
        const formattedAppointments = appointmentsResponse.data.map(appointment => ({
          ...appointment,
          title: `Rendez-vous: ${appointment.title || "Patient"}`,
          backgroundColor: "#FF7043", // Orange pour les rendez-vous
          borderColor: "#E64A19",
          textColor: "#FFFFFF",
          editable: false // Non modifiable
        }));
        setAppointments(formattedAppointments);
      }

      // 2. Récupérer les disponibilités
      const availabilitiesResponse = await api.get(`/clinic/doctorAvailability`, {
        params: {
          doctorId: doctorId,
          appointmentDate: date,
        }
      });

      if (availabilitiesResponse.data && availabilitiesResponse.data.availableSlots) {
        // Convertir les créneaux disponibles au format attendu par FullCalendar
        const formattedAvailabilities = availabilitiesResponse.data.availableSlots.map(slot => ({
          title: "Disponible",
          start: `${date}T${slot.start}:00`,
          end: `${date}T${slot.end}:00`,
          backgroundColor: "#4CAF50", // Vert pour les disponibilités
          borderColor: "#388E3C",
          textColor: "#FFFFFF",
          editable: true,
          duration: slot.duration
        }));
        setAvailabilities(formattedAvailabilities);
      }

      // 3. Récupérer les créneaux bloqués - on utilise la même API que pour les rendez-vous
      // mais les distingue visuellement
      const blockedResponse = await api.get(`/clinic/blockedSlots?doctorId=${doctorId}`);
      if (Array.isArray(blockedResponse.data)) {
        const formattedBlocked = blockedResponse.data.map(slot => ({
          ...slot,
          title: "Indisponible",
          backgroundColor: "#9E9E9E", // Gris pour les créneaux bloqués
          borderColor: "#616161",
          textColor: "#FFFFFF",
          editable: true
        }));
        setBlockedSlots(formattedBlocked);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du planning:", error);
    }
  };

  useEffect(() => {
    fetchDoctorSchedule();
  }, [doctorId]);

  // Mise à jour lors du changement de date dans le calendrier
  const handleDatesSet = (dateInfo) => {
    const startDate = dateInfo.start.toISOString().split('T')[0];
    setCurrentDate(startDate);
    fetchDoctorSchedule(startDate);
  };

  // Fonction pour gérer le clic sur une date/heure
  const handleDateClick = (info) => {
    const clickedDate = info.date.toISOString().split("T")[0];
    const clickedTime = info.date.toISOString().split("T")[1].substring(0, 5);
    
    setSelectedDate(clickedDate);
    setTimeRange({
      startTime: clickedTime,
      endTime: addMinutesToTime(clickedTime, 30)
    });
    setSelectedInfo(info);
    setShowAvailabilityModal(true);
  };

  // Ajouter des minutes à une heure au format HH:MM
  const addMinutesToTime = (time, minutes) => {
    const [hours, mins] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, mins + minutes);
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };

  // Ajout d'une disponibilité
  const handleAddAvailability = async () => {
    try {
      const { startTime, endTime } = timeRange;
      const startDateTime = `${selectedDate}T${startTime}:00`;
      const endDateTime = `${selectedDate}T${endTime}:00`;
      
      let dates = [{ start: startDateTime, end: endDateTime }];
      
      // Gestion de la récurrence
      if (recurrence !== "none" && recurrenceEndDate) {
        dates = generateRecurrentDates(
          selectedDate, 
          startTime, 
          endTime,
          recurrence, 
          recurrenceEndDate
        );
      }
      
      // Envoi de la disponibilité à l'API
      await api.post('/clinic/availability', {
        doctorId,
        availabilities: dates.map(date => ({
          date: date.start.split('T')[0],
          startTime: date.start.split('T')[1].substring(0, 5),
          endTime: date.end.split('T')[1].substring(0, 5),
          available: true
        }))
      });
      
      // Ajouter à l'état local
      const newAvailabilities = dates.map(date => ({
        title: "Disponible",
        start: date.start,
        end: date.end,
        backgroundColor: "#4CAF50",
        borderColor: "#388E3C",
        textColor: "#FFFFFF",
        editable: true
      }));
      
      setAvailabilities([...availabilities, ...newAvailabilities]);
      setShowAvailabilityModal(false);
      
      // Rafraîchir les données
      fetchDoctorSchedule(selectedDate);
    } catch (error) {
      console.error("Erreur lors de l'ajout de disponibilité:", error);
    }
  };

  // Bloquer un créneau
  const handleBlockTimeSlot = async () => {
    try {
      const { startTime, endTime } = timeRange;
      const startDateTime = `${selectedDate}T${startTime}:00`;
      const endDateTime = `${selectedDate}T${endTime}:00`;
      
      let dates = [{ start: startDateTime, end: endDateTime }];
      
      // Gestion de la récurrence
      if (recurrence !== "none" && recurrenceEndDate) {
        dates = generateRecurrentDates(
          selectedDate, 
          startTime, 
          endTime,
          recurrence, 
          recurrenceEndDate
        );
      }
      
      // Envoi du blocage à l'API
      await api.post('/clinic/blockedSlots', {
        doctorId,
        blockedSlots: dates.map(date => ({
          date: date.start.split('T')[0],
          startTime: date.start.split('T')[1].substring(0, 5),
          endTime: date.end.split('T')[1].substring(0, 5)
        }))
      });
      
      // Ajouter à l'état local
      const newBlockedSlots = dates.map(date => ({
        title: "Indisponible",
        start: date.start,
        end: date.end,
        backgroundColor: "#9E9E9E",
        borderColor: "#616161",
        textColor: "#FFFFFF",
        editable: true
      }));
      
      setBlockedSlots([...blockedSlots, ...newBlockedSlots]);
      setShowAvailabilityModal(false);
      
      // Rafraîchir les données
      fetchDoctorSchedule(selectedDate);
    } catch (error) {
      console.error("Erreur lors du blocage de créneau:", error);
    }
  };

  // Gestion du drag & drop d'événements
  const handleEventDrop = async (info) => {
    try {
      const { event } = info;
      const eventType = event.backgroundColor === "#4CAF50" ? "availability" : 
                        event.backgroundColor === "#9E9E9E" ? "blocked" : "appointment";
      
      // Si c'est un rendez-vous, ne pas permettre le déplacement
      if (eventType === "appointment") {
        info.revert();
        alert("Les rendez-vous ne peuvent pas être déplacés. Veuillez contacter le patient.");
        return;
      }
      
      const newStartDate = event.start.toISOString().split('T')[0];
      const newStartTime = event.start.toISOString().split('T')[1].substring(0, 5);
      const newEndTime = event.end ? event.end.toISOString().split('T')[1].substring(0, 5) : 
                        addMinutesToTime(newStartTime, 30);
      
      // Mettre à jour dans l'API
      if (eventType === "availability") {
        await api.put(`/clinic/availability/${event.id || 'update'}`, {
          doctorId,
          date: newStartDate,
          oldStartTime: event._def.extendedProps.originalStart ? 
                      event._def.extendedProps.originalStart.split('T')[1].substring(0, 5) : 
                      null,
          newStartTime: newStartTime,
          newEndTime: newEndTime,
          available: true
        });
      } else if (eventType === "blocked") {
        await api.put(`/clinic/blockedSlots/${event.id || 'update'}`, {
          doctorId,
          date: newStartDate,
          oldStartTime: event._def.extendedProps.originalStart ? 
                      event._def.extendedProps.originalStart.split('T')[1].substring(0, 5) : 
                      null,
          newStartTime: newStartTime,
          newEndTime: newEndTime
        });
      }
      
      // Rafraîchir les données
      fetchDoctorSchedule(newStartDate);
    } catch (error) {
      info.revert();
      console.error("Erreur lors du déplacement de l'événement:", error);
    }
  };

  // Supprimer un événement
  const handleEventClick = async (info) => {
    const { event } = info;
    const eventType = event.backgroundColor === "#4CAF50" ? "availability" : 
                      event.backgroundColor === "#9E9E9E" ? "blocked" : "appointment";
    
    // Si c'est un rendez-vous, ne pas permettre la suppression
    if (eventType === "appointment") {
      alert("Pour annuler un rendez-vous, veuillez contacter le patient.");
      return;
    }
    
    const confirmation = window.confirm(
      `Voulez-vous supprimer ce créneau ${eventType === "availability" ? "disponible" : "bloqué"} ?`
    );
    
    if (confirmation) {
      try {
        const eventDate = event.start.toISOString().split('T')[0];
        const eventStartTime = event.start.toISOString().split('T')[1].substring(0, 5);
        const eventEndTime = event.end ? event.end.toISOString().split('T')[1].substring(0, 5) : 
                           addMinutesToTime(eventStartTime, 30);
        
        // Supprimer de l'API
        if (eventType === "availability") {
          await api.delete(`/clinic/availability`, {
            data: {
              doctorId,
              date: eventDate,
              startTime: eventStartTime,
              endTime: eventEndTime
            }
          });
          // Mettre à jour l'état local
          setAvailabilities(availabilities.filter(a => 
            !(a.start === `${eventDate}T${eventStartTime}:00` && a.end === `${eventDate}T${eventEndTime}:00`)
          ));
        } else if (eventType === "blocked") {
          await api.delete(`/clinic/blockedSlots`, {
            data: {
              doctorId,
              date: eventDate,
              startTime: eventStartTime,
              endTime: eventEndTime
            }
          });
          // Mettre à jour l'état local
          setBlockedSlots(blockedSlots.filter(b => 
            !(b.start === `${eventDate}T${eventStartTime}:00` && b.end === `${eventDate}T${eventEndTime}:00`)
          ));
        }
        
        // Rafraîchir les données
        fetchDoctorSchedule(eventDate);
      } catch (error) {
        console.error("Erreur lors de la suppression de l'événement:", error);
      }
    }
  };

  // Générer des dates récurrentes
  const generateRecurrentDates = (startDate, startTime, endTime, recurrenceType, endDate) => {
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    while (start <= end) {
      // Pour récurrence hebdomadaire, ajouter seulement le même jour de la semaine
      if (recurrenceType === "weekly" && start.getDay() !== new Date(startDate).getDay()) {
        start.setDate(start.getDate() + 1);
        continue;
      }
      
      const currentDate = start.toISOString().split('T')[0];
      dates.push({
        start: `${currentDate}T${startTime}:00`,
        end: `${currentDate}T${endTime}:00`
      });
      
      // Incrémenter selon le type de récurrence
      if (recurrenceType === "daily") {
        start.setDate(start.getDate() + 1);
      } else if (recurrenceType === "weekly") {
        start.setDate(start.getDate() + 7);
      }
    }
    
    return dates;
  };

  // Tous les événements combinés
  const allEvents = [...appointments, ...availabilities, ...blockedSlots];

  return (
    <div>
      <h2 className="fw-bold" style={{ color: "#0056B3", fontSize: "42px", marginBottom: "40px" }}>
        Gestion de vos disponibilités
      </h2>

      <div className="d-flex mb-4">
        <div className="me-3">
          <span className="legend-item" style={{ backgroundColor: "#4CAF50", display: "inline-block", width: "20px", height: "20px", marginRight: "5px", borderRadius: "3px" }}></span> 
          <span>Disponible</span>
        </div>
        <div className="me-3">
          <span className="legend-item" style={{ backgroundColor: "#9E9E9E", display: "inline-block", width: "20px", height: "20px", marginRight: "5px", borderRadius: "3px" }}></span> 
          <span>Bloqué</span>
        </div>
        <div>
          <span className="legend-item" style={{ backgroundColor: "#FF7043", display: "inline-block", width: "20px", height: "20px", marginRight: "5px", borderRadius: "3px" }}></span> 
          <span>Rendez-vous</span>
        </div>
      </div>

      <div className="bg-white p-4 modalcalendar text-dark">
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={"timeGridWeek"}
          headerToolbar={{
            start: "today prev,next",
            center: "title",
            end: "timeGridDay,timeGridWeek,dayGridMonth",
          }}
          events={allEvents}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          datesSet={handleDatesSet}
          editable={true}
          selectable={true}
          selectMirror={true}
          height={"80vh"}
          slotMinTime="08:00:00"
          slotMaxTime="19:00:00"
          allDaySlot={false}
          slotDuration="00:15:00"
          eventTimeFormat={{
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }}
          businessHours={{
            daysOfWeek: [1, 2, 3, 4, 5], // Lundi à vendredi
            startTime: '08:00',
            endTime: '19:00',
          }}
          dayMaxEvents={true}
          locale="fr"
          firstDay={1} // La semaine commence le lundi (1) plutôt que le dimanche (0)
        />

        {/* Modal pour gérer les disponibilités */}
        {showAvailabilityModal && (
          <div className="modal show d-block">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Gérer la disponibilité</h5>
                  <button type="button" className="btn-close" onClick={() => setShowAvailabilityModal(false)}></button>
                </div>
                <div className="modal-body">
                  <p>Date sélectionnée: <strong>{selectedDate}</strong></p>
                  
                  <div className="mb-3">
                    <label className="form-label">Heure de début:</label>
                    <input
                      type="time"
                      className="form-control"
                      value={timeRange.startTime}
                      onChange={(e) => setTimeRange({...timeRange, startTime: e.target.value})}
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Heure de fin:</label>
                    <input
                      type="time"
                      className="form-control"
                      value={timeRange.endTime}
                      onChange={(e) => setTimeRange({...timeRange, endTime: e.target.value})}
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Récurrence:</label>
                    <select 
                      className="form-select"
                      value={recurrence}
                      onChange={(e) => setRecurrence(e.target.value)}
                    >
                      <option value="none">Pas de récurrence</option>
                      <option value="daily">Tous les jours</option>
                      <option value="weekly">Toutes les semaines (même jour)</option>
                    </select>
                  </div>
                  
                  {recurrence !== "none" && (
                    <div className="mb-3">
                      <label className="form-label">Jusqu'à:</label>
                      <input
                        type="date"
                        className="form-control"
                        value={recurrenceEndDate}
                        onChange={(e) => setRecurrenceEndDate(e.target.value)}
                        min={selectedDate}
                      />
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button 
                    className="btn btn-secondary" 
                    onClick={() => setShowAvailabilityModal(false)}
                  >
                    Annuler
                  </button>
                  <button 
                    className="btn btn-danger" 
                    onClick={handleBlockTimeSlot}
                  >
                    <FaLock className="me-1" /> Bloquer ce créneau
                  </button>
                  <button 
                    className="btn btn-success" 
                    onClick={handleAddAvailability}
                  >
                    <FaUnlock className="me-1" /> Définir comme disponible
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarView;